import { DslError, tokenize } from '@core/filters/path-filter/dsl/lexer';
import { DslParser } from '@core/filters/path-filter/dsl/parser';
import {
  OP_PHRASES,
  RELATIVE_DATE_DSL,
  printPath
} from '@core/filters/path-filter/dsl/printer';
import {
  printQualifierPath,
  printSearchBar,
  printShorthandWord
} from '@core/filters/path-filter/dsl/search-bar';
import {
  shorthandCodec,
  vocabularyShorthand
} from '@core/filters/path-filter/vocabulary/default-operators';
import {
  FieldType,
  FilterAssociation,
  FilterField,
  FilterVocabulary,
  SortableField
} from '@core/filters/path-filter/vocabulary/vocabulary';
import {
  FieldPath,
  FilterOpName,
  Quantifier,
  edgePath
} from '@core/filters/path-filter/wire';
import { foldForSearch } from '@core/utils/search-text.utils';

import { SortOption, sameSortTarget, sortOptions } from '../sort/sort-options';

export type SuggestionSection = 'fields' | 'edges' | 'operators' | 'sort' | 'values';

export interface SearchSuggestion {
  section: SuggestionSection;
  /** What gets inserted, as the bar spells it. */
  label: string;
  /** The human name of what `label` refers to. */
  detail: string | null;
  /** A field's type, for its icon. */
  type: FieldType | null;
  insert: string;
  from: number;
  to: number;
}

/** The shape a value takes when the field has no closed set to pick from. */
export type ValueHint =
  | 'number'
  | 'mass'
  | 'length'
  | 'duration'
  | 'date'
  | 'date_time'
  | 'text'
  | 'ids'
  | 'tags';

export interface SuggestionList {
  items: SearchSuggestion[];
  hint: ValueHint | null;
  /** Inside a `sort:` qualifier, where `-desc` is the one modifier the list cannot show per row. */
  sortContext: boolean;
  /** Something was typed to narrow the list, so its first row is what the user most likely wants. */
  narrowed: boolean;
}

export interface SuggestionLabels {
  field(field: FilterField): string;
  association(association: FilterAssociation): string;
  sortable(sortable: SortableField): string;
  aggregate(aggregate: 'min' | 'max'): string;
  relativeDate(dsl: string): string;
  operatorExample(op: FilterOpName): string;
  quantifier(quantifier: Quantifier): string;
}

export const MAX_SUGGESTIONS = 40;

const NONE: SuggestionList = { items: [], hint: null, sortContext: false, narrowed: false };
const NAME = '(?:[A-Za-z_][A-Za-z0-9_]*|`(?:[^`\\\\]|\\\\.)*`)';
/** A qualifier being given its value: `[-]path:` then the value typed so far, up to the caret. */
const QUALIFIER_VALUE = new RegExp(`(?:^|[\\s(])-?(${NAME}(?:\\.${NAME})*):([^\\s()]*)$`);
const SORT_VALUE = /(?:^|[\s(])sort:([^\s()]*)$/;
/** `kpi.` and the KPI name after it are one word: the start of the KPI field they name. */
const WORD = /(?:kpi\.)?(?:[A-Za-z_][A-Za-z0-9_]*)?$/;
const KPI_NAME = 'kpi\\.[A-Za-z_][A-Za-z0-9_]*(?:\\[[A-Za-z0-9_]+\\])?';
const SEGMENT = `(?:${KPI_NAME}|${NAME})`;
/**
 * A field path as typed, a keyed name being one backquoted segment or a keyed KPI, followed by the
 * space before its operator.
 */
const FIELD_AND_GAP = new RegExp(`(${SEGMENT}(?:\\.${SEGMENT})*)\\s+`, 'y');

export function searchSuggestions(
  text: string,
  caret: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels,
  /** Sort targets offered beyond the vocabulary's, such as the grid's KPI columns. */
  extraSort: SortOption[] = []
): SuggestionList {
  const before = text.slice(0, caret);
  if ((before.match(/"/g) ?? []).length % 2 === 1) return NONE;

  const sort = SORT_VALUE.exec(before);
  if (sort) return sortSuggestions(sort[1], caret, vocabulary, labels, extraSort);

  const qualifier = QUALIFIER_VALUE.exec(before);
  if (qualifier)
    return valueSuggestions(qualifier[1], qualifier[2], caret, vocabulary, labels);

  const operators = operatorSuggestions(before, caret, vocabulary, labels);
  if (operators) return operators;

  const word = WORD.exec(before)?.[0] ?? '';
  const from = caret - word.length;
  const lead = before.slice(0, from);
  // Only a boundary opens the list: the start, a space, `(`, or a negating `-` written at one. After
  // `.` the word is a path segment, after a letter or digit it is the middle of a search word.
  if (!/(?:^|[\s(])-?$/.test(lead)) return NONE;

  const edge = enclosingEdge(lead, vocabulary);
  const quantifiers = quantifierSuggestions(word, edge, caret, vocabulary, labels);
  const others =
    edge === null
      ? rootSuggestions(word, from, caret, vocabulary, labels)
      : edgeFieldSuggestions(edge, word, from, caret, vocabulary, labels);
  const items = [
    ...quantifiers,
    ...others.filter(({ insert }) => !insert.startsWith(`${word}[`))
  ];
  return { ...NONE, items: items.slice(0, MAX_SUGGESTIONS), narrowed: word.length > 0 };
}

/** A label operator is written after `label "<key>"`, so that is how its row reads. */
function operatorSpelling(op: FilterOpName): string {
  return op.startsWith('label_') ? `label "key" ${OP_PHRASES[op]}` : OP_PHRASES[op];
}

/**
 * The operators of the field named just before the word under the caret: `field ` lists them all,
 * `field bet` the ones the typed words start. Of several readings of the text as a field followed by
 * words, the first that names a field of the entity at the caret and a prefix of its operators wins.
 */
function operatorSuggestions(
  before: string,
  caret: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels
): SuggestionList | null {
  for (let start = 0; start < before.length; start++) {
    if (start > 0 && !/[\s(]/.test(before[start - 1])) continue;
    FIELD_AND_GAP.lastIndex = start;
    const read = FIELD_AND_GAP.exec(before);
    if (!read) continue;
    const typed = before.slice(start + read[0].length);
    const path = typedPath(read[1]);
    const edge = enclosingEdge(before.slice(0, start), vocabulary);
    const field = path && vocabulary.field(edge, path);
    if (!field) continue;
    const items = field.operators
      .map((op) => ({ op, spelling: operatorSpelling(op) }))
      .filter(({ spelling }) => spelling.toLowerCase().startsWith(typed.toLowerCase()))
      .map(
        ({ op, spelling }): SearchSuggestion => ({
          section: 'operators',
          label: spelling,
          detail: labels.operatorExample(op),
          type: null,
          insert: op.startsWith('label_') ? 'label "' : `${OP_PHRASES[op]} `,
          from: start + read[0].length,
          to: caret
        })
      );
    if (items.length === 0) continue;
    return { ...NONE, items, narrowed: typed.length > 0 };
  }
  return null;
}

/** The path `text` names, read as the bar reads it. */
function typedPath(text: string): FieldPath | null {
  try {
    const parser = new DslParser(tokenize(text, { searchBar: true }), text);
    const path = parser.parsePath();
    return parser.atEnd() ? path : null;
  } catch (error) {
    if (error instanceof DslError) return null;
    throw error;
  }
}

/** The quantifiers that can follow `edge` when the word is exactly that edge. */
function quantifierSuggestions(
  word: string,
  within: string | null,
  caret: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels
): SearchSuggestion[] {
  const association = vocabulary.associations(within).find(({ edge }) => edge === word);
  if (!association) return [];
  return association.quantifiers.map(
    (quantifier): SearchSuggestion => ({
      section: 'edges',
      label: `[${quantifier}](`,
      detail: labels.quantifier(quantifier),
      type: null,
      insert: `[${quantifier}](`,
      from: caret,
      to: caret
    })
  );
}

function matches(prefix: string, ...names: (string | null)[]): boolean {
  const folded = foldForSearch(prefix);
  const [spelling, ...labels] = names;
  return (
    foldForSearch(spelling).startsWith(folded) ||
    labels.some((label) => foldForSearch(label).includes(folded))
  );
}

function rootSuggestions(
  word: string,
  from: number,
  to: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels
): SearchSuggestion[] {
  const at = { from, to };
  const fields = vocabulary
    .fields(null)
    .map((field) => {
      const shorthand = shorthandCodec(field, vocabulary) !== null;
      // A field without a shorthand still takes the DSL form, `field <operator> value`.
      const insert = shorthand
        ? `${printQualifierPath(field.path)}:`
        : `${printPath(field.path)} `;
      return { field, shorthand, insert, detail: labels.field(field) };
    })
    .filter(({ insert, detail }) => matches(word, insert, detail))
    .sort((a, b) => Number(b.shorthand) - Number(a.shorthand))
    .map(
      ({ field, insert, detail }): SearchSuggestion => ({
        section: 'fields',
        label: insert.trimEnd(),
        detail,
        type: field.type,
        insert,
        ...at
      })
    );
  const edges = edgeSuggestions(null, word, from, to, vocabulary, labels);
  const sort: SearchSuggestion[] = 'sort'.startsWith(word.toLowerCase())
    ? [
        {
          section: 'sort',
          label: 'sort:',
          detail: null,
          type: null,
          insert: 'sort:',
          ...at
        }
      ]
    : [];
  return [...fields, ...edges, ...sort];
}

/** The associations a filter written in `within` may hold, each inserted as `edge[any](`. */
function edgeSuggestions(
  within: string | null,
  word: string,
  from: number,
  to: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels
): SearchSuggestion[] {
  return vocabulary
    .associations(within)
    .map((association) => {
      const quantifier = association.quantifiers.includes('any')
        ? 'any'
        : association.quantifiers[0];
      return {
        insert: `${association.edge}[${quantifier}](`,
        detail: labels.association(association),
        edge: association.edge
      };
    })
    .filter(({ edge, detail }) => matches(word, edge, detail))
    .map(
      ({ insert, detail }): SearchSuggestion => ({
        section: 'edges',
        label: `${insert}…)`,
        detail,
        type: null,
        insert,
        from,
        to
      })
    );
}

/** Inside `edge[any](…)` the target's fields, written in the DSL form, and its nested associations. */
function edgeFieldSuggestions(
  edge: string,
  word: string,
  from: number,
  to: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels
): SearchSuggestion[] {
  const fields = vocabulary
    .fields(edge)
    .map((field) => ({
      field,
      insert: `${printPath(field.path)} `,
      detail: labels.field(field)
    }))
    .filter(({ insert, detail }) => matches(word, insert, detail))
    .map(
      ({ field, insert, detail }): SearchSuggestion => ({
        section: 'fields',
        label: insert.trimEnd(),
        detail,
        type: field.type,
        insert,
        from,
        to
      })
    );
  return [...fields, ...edgeSuggestions(edge, word, from, to, vocabulary, labels)];
}

/**
 * The edge path of the innermost association whose parentheses are still open before the caret,
 * plain groups skipped; null at the root or when an open edge is one the vocabulary does not know.
 */
function enclosingEdge(lead: string, vocabulary: FilterVocabulary): string | null {
  const open: (string | null)[] = [];
  for (let i = 0; i < lead.length; i++) {
    if (lead[i] === '(') {
      open.push(
        /([A-Za-z_][A-Za-z0-9_]*)\[(?:any|all)\]$/.exec(lead.slice(0, i))?.[1] ?? null
      );
    } else if (lead[i] === ')') {
      open.pop();
    }
  }
  let path: string | null = null;
  for (const edge of open) {
    if (edge === null) continue;
    path = edgePath(path, edge);
    if (!vocabulary.association(path)) return null;
  }
  return path;
}

function valueSuggestions(
  pathText: string,
  valueText: string,
  caret: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels
): SuggestionList {
  const field = vocabulary
    .fields(null)
    .find((candidate) => printQualifierPath(candidate.path) === pathText);
  const codec = field ? shorthandCodec(field, vocabulary) : null;
  if (!field || !codec) return NONE;
  const typed = valueText.split(',');
  const partial = typed.pop() ?? '';
  const at = { from: caret - partial.length, to: caret };
  const value = (raw: string, detail: string | null): SearchSuggestion => ({
    section: 'values',
    label: printShorthandWord(raw),
    detail,
    type: null,
    insert: printShorthandWord(raw),
    ...at
  });
  const closed = closedSet(field, codec.op, labels);
  if (closed === null || valueText.includes('..')) {
    return { ...NONE, hint: valueHint(field.type, codec.op) };
  }
  // A list takes each value once; a single-value operator ends at the first one.
  if (typed.length > 0 && codec.op !== 'is_any_of') return NONE;
  const chosen = new Set(typed.map((item) => item.replace(/^"|"$/g, '')));
  const items = closed
    .filter(({ raw }) => !chosen.has(raw))
    .filter(({ raw, detail }) => matches(partial.replace(/^"/, ''), raw, detail))
    .map(({ raw, detail }) => value(raw, detail))
    .slice(0, MAX_SUGGESTIONS);
  return { ...NONE, items, narrowed: partial.length > 0 };
}

function closedSet(
  field: FilterField,
  op: string,
  labels: SuggestionLabels
): { raw: string; detail: string | null }[] | null {
  if (op === 'is_any_of' && field.enumValues?.length) {
    return field.enumValues.map((raw) => ({ raw, detail: null }));
  }
  if (op === 'equals' && field.type === 'BOOL') {
    return ['true', 'false'].map((raw) => ({ raw, detail: null }));
  }
  if (op === 'relative_date') {
    return Object.values(RELATIVE_DATE_DSL).map((raw) => ({
      raw,
      detail: labels.relativeDate(raw)
    }));
  }
  return null;
}

function valueHint(type: FieldType, op: string): ValueHint | null {
  switch (op) {
    case 'contains':
      return 'text';
    case 'has_any_tag':
      return 'tags';
    case 'is_any_of':
      return 'ids';
    case 'between':
      switch (type) {
        case 'MASS':
          return 'mass';
        case 'LENGTH':
          return 'length';
        case 'DURATION':
          return 'duration';
        case 'DATE':
          return 'date';
        case 'DATE_TIME':
          return 'date_time';
        default:
          return 'number';
      }
    default:
      return null;
  }
}

function sortSuggestions(
  partial: string,
  caret: number,
  vocabulary: FilterVocabulary,
  labels: SuggestionLabels,
  extraSort: SortOption[]
): SuggestionList {
  const resolver = vocabularyShorthand(vocabulary);
  const known = sortOptions(vocabulary, labels.sortable, labels.aggregate);
  const extra = extraSort.filter(
    (option) => !known.some((other) => sameSortTarget(other.target, option.target))
  );
  const items = [...known, ...extra]
    .map((option) => ({
      spelling: printSearchBar(
        { search: [], sort: [{ ...option.target, order: 'asc' }] },
        resolver
      ).slice('sort:'.length),
      label: option.label
    }))
    .filter(({ spelling, label }) => matches(partial, spelling, label))
    .map(
      ({ spelling, label }): SearchSuggestion => ({
        section: 'sort',
        label: spelling,
        detail: label,
        type: null,
        insert: spelling,
        from: caret - partial.length,
        to: caret
      })
    )
    .slice(0, MAX_SUGGESTIONS);
  return { ...NONE, items, sortContext: true, narrowed: partial.length > 0 };
}
